import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import DOMPurify from 'dompurify';
import type { DiskussionPostRef } from '../../types';
import { useAuthenticatedFilePreview } from '../../hooks/useAuthenticatedFilePreview';
import DiskussionChecklist from './DiskussionChecklist';

const MENTION_REGEX = /@m(\d+)\b/g;
const INLINE_REF_REGEX = /\[\[aio:([a-z_]+):([^\]]+)\]\]/g;
const MARKER_SPLIT = /(@m\d+\b|\[\[aio:[a-z_]+:[^\]]+\]\])/g;
const FILES_PREVIEW_RE = /\/files\/(\d+)\/preview/;
const CHECKLIST_MARKER_RE = /\[\[aio:checklist:(\d+)\]\]/g;

export interface DiskussionPostBodyProps {
  body: string;
  refs: DiskussionPostRef[];
  /** Wenn true (z. B. gesperrter Thread fuer Nicht-Mods), Checklisten als read-only rendern. */
  readOnlyChecklist?: boolean;
}

const DiskussionInlineImage: React.FC<{ fileId: number; alt?: string }> = ({ fileId, alt }) => {
  const url = useAuthenticatedFilePreview(fileId);
  if (!url) {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          minWidth: 120,
          minHeight: 72,
          bgcolor: 'action.hover',
          borderRadius: 1,
          verticalAlign: 'middle',
        }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt={alt ?? ''}
      sx={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1, my: 0.5 }}
    />
  );
};

function findRefForAioMarker(
  typ: string,
  raw: string,
  refs: DiskussionPostRef[],
): DiskussionPostRef | undefined {
  const trimmed = raw.trim();
  if (typ === 'hof_uebersicht' && trimmed === '0') {
    return refs.find((x) => x.ref_type === 'hof_uebersicht');
  }
  if (typ === 'hof_monat_mitarbeiter' && trimmed.includes('@')) {
    const [midS, ym] = trimmed.split('@', 2);
    const parts = ym.trim().split('-');
    if (parts.length === 2) {
      const mid = Number(midS.trim());
      const hj = Number(parts[0]);
      const hm = Number(parts[1]);
      if (Number.isFinite(mid) && Number.isFinite(hj) && Number.isFinite(hm)) {
        return refs.find(
          (x) =>
            x.ref_type === 'hof_monat_mitarbeiter' &&
            (x.ref_id === mid || x.hof_mitarbeiter_id === mid) &&
            x.hof_jahr === hj &&
            x.hof_monat === hm,
        );
      }
    }
    return undefined;
  }
  const id = Number(trimmed);
  if (!Number.isFinite(id)) return undefined;
  return refs.find((x) => x.ref_type === typ && x.ref_id === id);
}

/** Textfragment mit @m… und [[aio:…]] als React-Nodes rendern.
 *
 * Hinweis: Checklisten-Marker werden NICHT hier behandelt, sondern eine Ebene hoeher
 * als Block-Element gerendert (siehe Body-Renderer).
 */
function renderTextWithMarkers(text: string, refs: DiskussionPostRef[]): React.ReactNode[] {
  if (!text) return [];
  const parts = text.split(MARKER_SPLIT);
  return parts.map((part, i) => {
    const mMen = part.match(/^@m(\d+)\b$/);
    if (mMen) {
      const id = Number(mMen[1]);
      const r = refs.find((x) => x.ref_type === 'mitarbeiter' && x.ref_id === id);
      return (
        <Link key={i} component={RouterLink} to={r?.path ?? `/mitarbeiter/${id}`} sx={{ fontWeight: 600 }}>
          {r?.label ?? `@m${id}`}
        </Link>
      );
    }
    const mAio = part.match(/^\[\[aio:([a-z_]+):([^\]]+)\]\]$/);
    if (mAio) {
      const [, typ, rawVal] = mAio;
      if (typ === 'checklist') {
        return null;
      }
      if (typ === 'diskussion_datei') {
        const fid = Number(rawVal.trim());
        if (Number.isFinite(fid)) {
          return <DiskussionInlineImage key={i} fileId={fid} />;
        }
      }
      const r = findRefForAioMarker(typ, rawVal, refs);
      if (r) {
        return (
          <Link key={i} component={RouterLink} to={r.path} sx={{ fontWeight: 500 }}>
            {r.label}
          </Link>
        );
      }
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/** Liefert die postId aus einem reinen Checklisten-Marker-Knoten oder null, wenn keiner gefunden. */
function extractChecklistPostId(text: string | null | undefined): number | null {
  if (!text) return null;
  const m = text.trim().match(/^\[\[aio:checklist:(\d+)\]\]$/);
  if (!m) return null;
  const pid = Number(m[1]);
  return Number.isFinite(pid) ? pid : null;
}

function isHtmlBody(body: string): boolean {
  if (!body) return false;
  return /<(p|div|br|img|ul|ol|li|h[1-6]|strong|em|u|s|pre|code|blockquote|table|span)[\s/>]/i.test(body);
}

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'strike', 'b', 'i',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'code', 'pre',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'a', 'img', 'span', 'div', 'hr',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan', 'style', 'class'];
const VOID_TAGS = new Set(['br', 'hr']);

/** HTML-Node rekursiv zu React-Nodes mit Auth-Bildern und linkifizierten Markern. */
function renderHtmlNode(
  node: Node,
  keyPrefix: string,
  refs: DiskussionPostRef[],
  readOnlyChecklist: boolean,
): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return renderTextWithMarkers((node as Text).data, refs);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  if ((tag === 'p' || tag === 'div') && el.childElementCount === 0) {
    const pid = extractChecklistPostId(el.textContent);
    if (pid != null) {
      return <DiskussionChecklist key={keyPrefix} postId={pid} readOnly={readOnlyChecklist} />;
    }
  }

  if (VOID_TAGS.has(tag)) {
    return React.createElement(tag, { key: keyPrefix });
  }

  const children: React.ReactNode[] = [];
  el.childNodes.forEach((child, i) => {
    children.push(
      <React.Fragment key={`${keyPrefix}-${i}`}>
        {renderHtmlNode(child, `${keyPrefix}-${i}`, refs, readOnlyChecklist)}
      </React.Fragment>,
    );
  });

  if (tag === 'img') {
    const src = el.getAttribute('src') || '';
    const alt = el.getAttribute('alt') || '';
    const m = src.match(FILES_PREVIEW_RE);
    if (m) {
      return <DiskussionInlineImage key={keyPrefix} fileId={Number(m[1])} alt={alt} />;
    }
    return (
      <Box
        key={keyPrefix}
        component="img"
        src={src}
        alt={alt}
        sx={{ maxWidth: '100%', height: 'auto', display: 'block', borderRadius: 1, my: 0.5 }}
      />
    );
  }

  if (tag === 'a') {
    const href = el.getAttribute('href') || '#';
    return (
      <a key={keyPrefix} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  const style: React.CSSProperties = {};
  const styleAttr = el.getAttribute('style') || '';
  const alignMatch = styleAttr.match(/text-align\s*:\s*(left|right|center|justify)/i);
  if (alignMatch) {
    style.textAlign = alignMatch[1].toLowerCase() as React.CSSProperties['textAlign'];
  }

  const commonProps: { key: string; style?: React.CSSProperties; colSpan?: number; rowSpan?: number } = {
    key: keyPrefix,
  };
  if (Object.keys(style).length > 0) commonProps.style = style;
  const colSpan = el.getAttribute('colspan');
  const rowSpan = el.getAttribute('rowspan');
  if (colSpan) commonProps.colSpan = Number(colSpan);
  if (rowSpan) commonProps.rowSpan = Number(rowSpan);

  return React.createElement(tag, commonProps, children);
}

/** Splittet Plain-Text-Body an Checklisten-Markern und rendert dazwischen normale Marker. */
function renderPlainTextWithChecklist(
  body: string,
  refs: DiskussionPostRef[],
  readOnlyChecklist: boolean,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  const re = new RegExp(CHECKLIST_MARKER_RE.source, 'g');
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(body)) !== null) {
    if (match.index > lastIndex) {
      const segment = body.slice(lastIndex, match.index);
      out.push(
        <Box
          key={`t${key++}`}
          component="div"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}
        >
          {renderTextWithMarkers(segment, refs)}
        </Box>,
      );
    }
    const pid = Number(match[1]);
    if (Number.isFinite(pid)) {
      out.push(
        <DiskussionChecklist key={`c${key++}`} postId={pid} readOnly={readOnlyChecklist} />,
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) {
    const segment = body.slice(lastIndex);
    out.push(
      <Box
        key={`t${key++}`}
        component="div"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}
      >
        {renderTextWithMarkers(segment, refs)}
      </Box>,
    );
  }
  return out;
}

/** Rendert Beitrag: HTML-Bodies sicher (DOMPurify + Auth-Bilder); Plain-Text wie bisher mit Markern. */
const DiskussionPostBody: React.FC<DiskussionPostBodyProps> = ({
  body,
  refs,
  readOnlyChecklist = false,
}) => {
  const rendered = useMemo<React.ReactNode>(() => {
    if (!body) return null;
    if (!isHtmlBody(body)) {
      return renderPlainTextWithChecklist(body, refs, readOnlyChecklist);
    }
    const clean = DOMPurify.sanitize(body, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
    });
    const doc = new DOMParser().parseFromString(`<div>${clean}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return null;
    const nodes: React.ReactNode[] = [];
    root.childNodes.forEach((child, i) => {
      nodes.push(
        <React.Fragment key={i}>
          {renderHtmlNode(child, `n${i}`, refs, readOnlyChecklist)}
        </React.Fragment>,
      );
    });
    return nodes;
  }, [body, refs, readOnlyChecklist]);

  return (
    <Box
      className="diskussion-post-body"
      sx={{
        mt: 1,
        minWidth: 0,
        maxWidth: '100%',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        '& p': { my: 0.5 },
        '& ul, & ol': { pl: 3, my: 0.5 },
        '& blockquote': {
          borderLeft: (t) => `3px solid ${t.palette.primary.main}`,
          pl: 1.5,
          my: 0.5,
          color: 'text.secondary',
          fontStyle: 'italic',
        },
        '& code': {
          bgcolor: 'action.hover',
          borderRadius: 0.5,
          px: 0.5,
          py: 0.1,
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& :not(pre) > code': {
          overflowWrap: 'anywhere',
        },
        '& pre': {
          bgcolor: 'action.hover',
          borderRadius: 1,
          p: 1,
          overflow: 'auto',
          maxWidth: '100%',
        },
        '& table': {
          borderCollapse: 'collapse',
          my: 0.5,
          width: '100%',
          tableLayout: 'fixed',
          '& th, & td': {
            border: (t) => `1px solid ${t.palette.divider}`,
            p: 0.75,
            overflowWrap: 'anywhere',
          },
          '& th': { bgcolor: 'action.hover', fontWeight: 600 },
        },
        '& img': { maxWidth: '100%' },
      }}
    >
      {rendered}
    </Box>
  );
};

// Exported for tests / debugging
export { MENTION_REGEX, INLINE_REF_REGEX };

export default DiskussionPostBody;
